import type {
	FCSenderStatus,
	Level,
	ListedPlayerStateForSchema,
	MuleSenderItem,
	PaymentFailure,
	PaymentStatus,
	SenderPriorityUsage,
	TransferPlayerState,
	WithdrawalState,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks";
import type { TranslationKeys } from "@/hooks/useTranslation";
import type { IconsName } from "@st-marketplace/ui";
import type { ChipVariant } from "@st-marketplace/ui";
import { useMemo } from "react";

export const usePurchaseStatusConfig = () => {
	const { t } = useTranslation();

	const purchaseStatusMap: Record<
		ListedPlayerStateForSchema,
		{
			label: string;
			description: string;
			variant: ChipVariant;
			icon: IconsName;
		}
	> = {
		PURCHASED_AND_APPROVED: {
			label: t("playersAuction.purchaseStatus.purchasedAndApproved"),
			description: t(
				"playersAuction.purchaseStatus.purchasedAndApprovedDescription",
			),
			variant: "success",
			icon: "question-line",
		},
		PURCHASED: {
			label: t("playersAuction.purchaseStatus.purchased"),
			description: t("playersAuction.purchaseStatus.purchasedDescription"),
			variant: "info",
			icon: "question-line",
		},
		FAKE_PURCHASED: {
			label: t("playersAuction.purchaseStatus.fakePurchased"),
			description: t("playersAuction.purchaseStatus.fakePurchasedDescription"),
			variant: "error",
			icon: "question-line",
		},
		EXPIRED: {
			label: t("playersAuction.purchaseStatus.expired"),
			description: t("playersAuction.purchaseStatus.expiredDescription"),
			variant: "muted",
			icon: "question-line",
		},
		NOT_PURCHASED: {
			label: t("playersAuction.purchaseStatus.notPurchased"),
			description: t("playersAuction.purchaseStatus.notPurchasedDescription"),
			variant: "warning",
			icon: "question-line",
		},
		INIT: {
			label: t("playersAuction.purchaseStatus.init"),
			description: t("playersAuction.purchaseStatus.initDescription"),
			variant: "info",
			icon: "question-line",
		},
	};

	return (state: ListedPlayerStateForSchema) =>
		purchaseStatusMap[state] ?? {
			label: state,
			description: t("playersAuction.purchaseStatus.unknown"),
			variant: "muted",
			icon: "question-line",
		};
};

export const useWithdrawalStatusConfig = () => {
	const { t } = useTranslation();

	const withdrawalStatusMap: Record<
		Exclude<PaymentStatus, null> | "NULL" | "UNKNOWN",
		{ label: string; variant: ChipVariant }
	> = {
		SUCCESS: {
			label: t("playersAuction.withdrawalStatus.paid"),
			variant: "success",
		},
		PENDING: {
			label: t("playersAuction.withdrawalStatus.notPaid"),
			variant: "muted",
		},
		FAILED: {
			label: t("playersAuction.withdrawalStatus.failed"),
			variant: "error",
		},
		NULL: {
			label: t("playersAuction.withdrawalStatus.notRequested"),
			variant: "muted",
		},
		UNKNOWN: {
			label: t("playersAuction.withdrawalStatus.pending"),
			variant: "muted",
		},
	};

	return (status: PaymentStatus | null) =>
		withdrawalStatusMap[status ?? "NULL"] ?? withdrawalStatusMap.UNKNOWN;
};

export const useWithdrawalStateConfig = () => {
	const { t } = useTranslation();

	const withdrawalStateMap: Record<
		WithdrawalState,
		{ label: string; variant: ChipVariant }
	> = {
		paid: {
			label: t("playersAuction.withdrawalStatus.paid"),
			variant: "success",
		},
		not_paid: {
			label: t("playersAuction.withdrawalStatus.notPaid"),
			variant: "muted",
		},
		failed: {
			label: t("playersAuction.withdrawalStatus.failed"),
			variant: "error",
		},
		no_request: {
			label: t("playersAuction.withdrawalStatus.notRequested"),
			variant: "muted",
		},
	};

	return (status: WithdrawalState) =>
		withdrawalStateMap[status] ?? withdrawalStateMap.no_request;
};

export const useTransferPlayerStatusConfig = () => {
	const { t } = useTranslation();

	const transferPlayerStatusMap: Record<
		TransferPlayerState,
		{ label: string; variant: ChipVariant }
	> = {
		ACTIVE: {
			label: t("adminTransferPlayers.status.ACTIVE"),
			variant: "success",
		},
		DEACTIVATED_MANUALLY: {
			label: t("adminTransferPlayers.status.DEACTIVATED_MANUALLY"),
			variant: "warning",
		},
		DEACTIVATED_TOO_MANY_ERROR: {
			label: t("adminTransferPlayers.status.DEACTIVATED_TOO_MANY_ERROR"),
			variant: "error",
		},
		DEACTIVATED_PRICE_UNFAVORABLE: {
			label: t("adminTransferPlayers.status.DEACTIVATED_PRICE_UNFAVORABLE"),
			variant: "warning",
		},
	};

	return (status: TransferPlayerState) =>
		transferPlayerStatusMap[status] ?? {
			label: status,
			variant: "muted",
		};
};

export const useSenderStatusConfig = () => {
	const { t } = useTranslation();

	const senderStatusMap: Record<
		FCSenderStatus,
		{
			label: string;
			variant: ChipVariant;
			icon: IconsName;
			description: string;
		}
	> = {
		WRONG_EMAIL_PASSWORD: {
			label: t("senderStatus.wrongEmailPassword"),
			variant: "error",
			icon: "information-line",
			description: t("senderStatus.wrongEmailPasswordDescription"),
		},
		READY: {
			label: t("senderStatus.ready"),
			variant: "success",
			icon: "check-line",
			description: t("senderStatus.readyDescription"),
		},
		IN_QUEUE: {
			label: t("senderStatus.inQueue"),
			variant: "info",
			icon: "time-line",
			description: t("senderStatus.inQueueDescription"),
		},
		NO_PROXY_AVAILABLE: {
			label: t("senderStatus.noProxyAvailable"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.noProxyAvailableDescription"),
		},
		TRADING: {
			label: t("senderStatus.trading"),
			variant: "success",
			icon: "exchange-line",
			description: t("senderStatus.tradingDescription"),
		},
		STOPPED: {
			label: t("senderStatus.stopped"),
			variant: "warning",
			icon: "stop-line",
			description: t("senderStatus.stoppedDescription"),
		},
		PROCESSING: {
			label: t("senderStatus.processing"),
			variant: "info",
			icon: "loader-line",
			description: t("senderStatus.processingDescription"),
		},
		FAILED_TO_FETCH_SID: {
			label: t("senderStatus.failedToFetchSid"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.failedToFetchSidDescription"),
		},
		WRONG_BACKUP_FORMAT: {
			label: t("senderStatus.wrongBackupFormat"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.wrongBackupFormatDescription"),
		},
		WRONG_GAUTH_FORMAT: {
			label: t("senderStatus.wrongGAuthFormat"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.wrongGAuthFormatDescription"),
		},
		WRONG_CODE: {
			label: t("senderStatus.wrongCode"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.wrongCodeDescription"),
		},
		NO_CLUB: {
			label: t("senderStatus.noClub"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.noClubDescription"),
		},
		NO_AUTH_PROVIDED: {
			label: t("senderStatus.noAuthProvided"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.noAuthProvidedDescription"),
		},
		ORIGIN_PASSWORD_RESET: {
			label: t("senderStatus.originPasswordReset"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.originPasswordResetDescription"),
		},
		LOGIN_LIMIT_REACHED: {
			label: t("senderStatus.loginLimitReached"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.loginLimitReachedDescription"),
		},
		CONSOLE_ONLINE: {
			label: t("senderStatus.consoleOnline"),
			variant: "success",
			icon: "check-line",
			description: t("senderStatus.consoleOnlineDescription"),
		},
		WATCHLIST_FULL: {
			label: t("senderStatus.watchlistFull"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.watchlistFullDescription"),
		},
		CLUB_SETUP_REQUIRED: {
			label: t("senderStatus.clubSetupRequired"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.clubSetupRequiredDescription"),
		},
		TM_LIST_FULL: {
			label: t("senderStatus.tmListFull"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.tmListFullDescription"),
		},
		TM_LOCKED: {
			label: t("senderStatus.tmLocked"),
			variant: "warning",
			icon: "lock-line",
			description: t("senderStatus.tmLockedDescription"),
		},
		NO_TM_UNLOCKED_YET: {
			label: t("senderStatus.noTmUnlockedYet"),
			variant: "info",
			icon: "time-line",
			description: t("senderStatus.noTmUnlockedYetDescription"),
		},
		ONLY_WEB_TM_LOCKED: {
			label: t("senderStatus.onlyWebTmLocked"),
			variant: "warning",
			icon: "lock-line",
			description: t("senderStatus.onlyWebTmLockedDescription"),
		},
		ORIGIN_DISABLED: {
			label: t("senderStatus.originDisabled"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.originDisabledDescription"),
		},
		DEVICE_SUSPENDED: {
			label: t("senderStatus.deviceSuspended"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.deviceSuspendedDescription"),
		},
		UNEXPECTED_ERROR: {
			label: t("senderStatus.operationFailed"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.operationFailedDescription"),
		},
		CAPTCHA_SOLVER_FAILED: {
			label: t("senderStatus.captchaSolverFailed"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.captchaSolverFailedDescription"),
		},
		TEMP_BANNED: {
			label: t("senderStatus.tempBanned"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.tempBannedDescription"),
		},
		ONLINE_IN_WEBAPP: {
			label: t("senderStatus.onlineInWebapp"),
			variant: "success",
			icon: "check-line",
			description: t("senderStatus.onlineInWebappDescription"),
		},
		EA_SERVER_ERROR: {
			label: t("senderStatus.eaServerError"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.eaServerErrorDescription"),
		},
		CONSOLE_NOT_SUPPORTED: {
			label: t("senderStatus.consoleNotSupported"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.consoleNotSupportedDescription"),
		},
		NOT_ENOUGH_COIN: {
			label: t("senderStatus.notEnoughCoin"),
			variant: "warning",
			icon: "information-line",
			description: t("senderStatus.notEnoughCoinDescription"),
		},
		SLEEP: {
			label: t("senderStatus.sleep"),
			variant: "muted",
			icon: "moon-line",
			description: t("senderStatus.sleepDescription"),
		},
		CLEANING_TM: {
			label: t("senderStatus.cleaningTm"),
			variant: "info",
			icon: "loader-line",
			description: t("senderStatus.cleaningTmDescription"),
		},
		EARLY_ACCESS_EXPIRED: {
			label: t("senderStatus.earlyAccessExpired"),
			variant: "warning",
			icon: "time-line",
			description: t("senderStatus.earlyAccessExpiredDescription"),
		},
		EA_SERVER_BROKEN: {
			label: t("senderStatus.eaServerBroken"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.eaServerBrokenDescription"),
		},
		LOGIN_FAILED: {
			label: t("senderStatus.loginFailed"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.loginFailedDescription"),
		},
		PROXY_NOT_WORKING: {
			label: t("senderStatus.proxyNotWorking"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.proxyNotWorkingDescription"),
		},
		TWO_FA_BLOCKED: {
			label: t("senderStatus.twoFaBlocked"),
			variant: "error",
			icon: "error-warning-line",
			description: t("senderStatus.twoFaBlockedDescription"),
		},
	};

	return (status: FCSenderStatus) =>
		senderStatusMap[status] ?? {
			label: status,
			variant: "muted" as ChipVariant,
			icon: "question-line" as IconsName,
			description: status,
		};
};

type PaymentStatusWithoutNull = Exclude<PaymentStatus, null>;

type PaymentStatusConfig = {
	icon: IconsName;
	variant: ChipVariant;
	labelKey: TranslationKeys;
};

type PaymentStatusLabelKeyMap = Record<
	PaymentStatusWithoutNull,
	TranslationKeys
>;

const paymentStatusBaseConfig: Record<
	PaymentStatusWithoutNull,
	Omit<PaymentStatusConfig, "labelKey">
> = {
	PENDING: {
		icon: "loader-2-line",
		variant: "info",
	},
	SUCCESS: {
		icon: "checkbox-circle-line",
		variant: "success",
	},
	FAILED: {
		icon: "alert-line",
		variant: "error",
	},
};

export const walletPaymentStatusLabelKeys = {
	PENDING: "wallet.withdrawalHistoryTable.statuses.pending",
	SUCCESS: "wallet.withdrawalHistoryTable.statuses.paid",
	FAILED: "wallet.withdrawalHistoryTable.statuses.failed",
} as const satisfies PaymentStatusLabelKeyMap;

export const poPaymentStatusLabelKeys = {
	PENDING: "paymentHistoryPage.table.statuses.pending",
	SUCCESS: "paymentHistoryPage.table.statuses.success",
	FAILED: "paymentHistoryPage.table.statuses.failed",
} as const satisfies PaymentStatusLabelKeyMap;

export const usePaymentStatusConfig = (labelKeys: PaymentStatusLabelKeyMap) => {
	return (status: PaymentStatus | null | undefined) => {
		if (!status) return undefined;

		const baseConfig = paymentStatusBaseConfig[status];
		const labelKey = labelKeys[status];

		if (!baseConfig || !labelKey) return undefined;

		return {
			...baseConfig,
			labelKey,
		};
	};
};
export const useLevelLabel = () => {
	const { t } = useTranslation();

	const levelLabelsWithLevel: Record<Level, string> = {
		BRONZE: t("level.Label.Bronze"),
		SILVER: t("level.Label.Silver"),
		GOLD: t("level.Label.Gold"),
		ELITE: t("level.Label.Elite"),
	};

	const levelLabelsWithoutLevel: Record<Level, string> = {
		BRONZE: t("level.LabelWithoutLevel.Bronze"),
		SILVER: t("level.LabelWithoutLevel.Silver"),
		GOLD: t("level.LabelWithoutLevel.Gold"),
		ELITE: t("level.LabelWithoutLevel.Elite"),
	};

	const getLevelLabel = (lvl?: Level, withoutLevel = false) => {
		if (!lvl) return "";
		return withoutLevel
			? levelLabelsWithoutLevel[lvl]
			: levelLabelsWithLevel[lvl];
	};

	return getLevelLabel;
};

type PaymentFailureTypeWithoutText = Exclude<PaymentFailure["type"], "OTHERS">;

export const useWithdrawalFailureReasonMap = () => {
	const { t } = useTranslation();

	return useMemo<Record<PaymentFailureTypeWithoutText, string>>(
		() => ({
			CONTACT_SUPPORT_ASAP: t(
				"wallet.withdrawalDetail.failureReasons.contact_support_asap",
			),
			REVERTED: t("wallet.withdrawalDetail.failureReasons.reverted"),
			WRONG_ACCOUNT_HOLDER_NAME: t(
				"wallet.withdrawalDetail.failureReasons.wrong_account_holder_name",
			),
			WRONG_BANK_ACCOUNT: t(
				"wallet.withdrawalDetail.failureReasons.wrong_bank_account",
			),
			WRONG_SHEBA: t("wallet.withdrawalDetail.failureReasons.wrong_sheba"),
		}),
		[t],
	);
};

export const useSenderPriorityMap = () => {
	const { t } = useTranslation();

	return useMemo<Record<SenderPriorityUsage, string>>(
		() => ({
			EXCLUDED: t("enums.senderPriority.excluded"),
			LOW: t("enums.senderPriority.low"),
			MEDIUM: t("enums.senderPriority.medium"),
			HIGH: t("enums.senderPriority.high"),
			VERY_HIGH: t("enums.senderPriority.veryHigh"),
		}),
		[t],
	);
};

type MuleStatusConfig = {
	label: string;
	variant: ChipVariant;
	icon: IconsName;
	description: string;
	/** Why it failed (EA login error / external error), only set for failures */
	reason?: string;
};

/**
 * فروش سریع status as the user should read it. QUEUED is split by our own login check, and only
 * FINISHED is shown as done (success) - an account in progress never looks finished.
 */
export const useMuleStatusConfig = () => {
	const { t } = useTranslation();
	const getSenderStatusConfig = useSenderStatusConfig();

	return (
		item: Pick<MuleSenderItem, "status" | "login_status" | "error">,
	): MuleStatusConfig => {
		switch (item.status) {
			case "QUEUED":
				switch (item.login_status) {
					case null:
						return {
							label: t("fastSell.status.waitingForCheck"),
							variant: "muted",
							icon: "time-line",
							description: t("fastSell.status.waitingForCheckDescription"),
						};
					case "PROCESSING":
						return {
							label: t("fastSell.status.checking"),
							variant: "info",
							icon: "loader-2-line",
							description: t("fastSell.status.checkingDescription"),
						};
					case "READY":
						return {
							label: t("fastSell.status.checked"),
							variant: "info",
							icon: "time-line",
							description: t("fastSell.status.checkedDescription"),
						};
					default:
						return {
							label: t("fastSell.status.loginFailed"),
							variant: "error",
							icon: "error-warning-line",
							description: t("fastSell.status.loginFailedDescription"),
							reason: getSenderStatusConfig(item.login_status as FCSenderStatus)
								.label,
						};
				}
			case "SENT":
				return {
					label: t("fastSell.status.sent"),
					variant: "info",
					icon: "time-line",
					description: t("fastSell.status.sentDescription"),
				};
			case "IN_USE":
				return {
					label: t("fastSell.status.inUse"),
					variant: "info",
					icon: "loader-2-line",
					description: t("fastSell.status.inUseDescription"),
				};
			case "ERROR":
				return {
					label: t("fastSell.status.error"),
					variant: "error",
					icon: "error-warning-line",
					description: t("fastSell.status.errorDescription"),
					reason: item.error
						? getSenderStatusConfig(item.error as FCSenderStatus).label
						: undefined,
				};
			case "FINISHED":
				return {
					label: t("fastSell.status.finished"),
					variant: "success",
					icon: "checkbox-circle-line",
					description: t("fastSell.status.finishedDescription"),
				};
			case "MOVED_TO_SYSTEM":
				return {
					label: t("fastSell.status.movedToSystem"),
					variant: "muted",
					icon: "arrow-left-right-line",
					description: t("fastSell.status.movedToSystemDescription"),
				};
		}
	};
};
